import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Plus, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/templates`, { withCredentials: true });
      setTemplates(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/templates/${id}`, { withCredentials: true });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const showPreview = (tpl) => {
    setPreviewHtml(tpl.html_content || '<p>No content</p>');
    setPreviewOpen(true);
  };

  return (
    <div className="flex-1 p-8" data-testid="templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>Templates</h1>
          <p className="text-[#B0B0B0] text-sm mt-1">Saved email templates</p>
        </div>
        <p className="text-sm text-[#808080]">Templates are saved from the Email Builder</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t, i) => (
          <div key={t.template_id} className={`bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5 animate-fade-in stagger-${(i % 4) + 1}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-[#00FF99]/10 flex items-center justify-center">
                  <FileText size={20} className="text-[#00FF99]" />
                </div>
                <div>
                  <h3 className="text-white font-bold">{t.name}</h3>
                  <p className="text-xs text-[#808080]">{t.category || 'custom'}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={() => showPreview(t)}
                className="bg-transparent border border-[rgba(255,255,255,0.12)] text-white hover:border-[#00FF99] hover:text-[#00FF99] text-xs">
                <Eye size={12} className="mr-1" /> Preview
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(t.template_id)}
                className="text-[#B0B0B0] hover:text-[#FF3B30] text-xs ml-auto">
                <Trash2 size={12} />
              </Button>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#808080]">
            <FileText size={32} className="mx-auto mb-3 opacity-50" />
            <p>No templates yet. Save templates from the Email Builder.</p>
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#f4f4f4] border-[rgba(255,255,255,0.12)] max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-black" style={{ fontFamily: 'Manrope' }}>Template Preview</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            <iframe srcDoc={previewHtml} title="Preview" className="w-full min-h-[500px] bg-white rounded" sandbox="allow-same-origin" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
